<template>
  <ReportWrapper
    :loading="loading"
    :on-load="loadReport"
    :tab-lv1-index="tabLv1Index"
    :tab-lv2-index="tabLv2Index"
    :showProviderFilter="isAdmin"
  >
    <ReportTable minWidth="1190px">
      <thead>
        <tr>
          <th><span>Name</span></th>
          <th>
            <p>Diagnosed</p>
            <span class="text-xs font-normal">per Recall patient</span>
          </th>
          <th>
            <TableHeaderWithTooltip
              title="Diagnosed"
              :tooltip="tooltips['New patients']"
            />
            <span class="text-xs font-normal">per New patient</span>
          </th>
          <th><span>Total diagnosed</span></th>
          <th><span>Completed</span></th>
          <th>
            <TableHeaderWithTooltip
              title="Acceptance %"
              :tooltip="tooltips['Treatment-based acceptance rate']"
            />
            <span class="text-xs font-normal">treatment based</span>
          </th>
          <th>
            <TableHeaderWithTooltip
              title="Acceptance %"
              :tooltip="tooltips['Patient-based acceptance rate']"
            />
            <span class="text-xs font-normal">patient based</span>
          </th>
        </tr>
      </thead>
      <tbody v-if="!loading">
        <!-- clientAdmin view -->
        <template v-if="isAdmin && treatmentsPerProvider.length">
          <tr
            v-for="(item, index) in treatmentsPerProvider"
            :key="index"
            :class="{
              active: item.isCurrentUserRecord,
              median: item.isMedian,
              hidden: !item.isActive || item.isHidden || item.isHiddenReport,
            }"
          >
            <td>{{ item.fullName }}</td>
            <td>{{ valuePerPatient(item.diagnosedPerRecallPatient) }}</td>
            <td>{{ valuePerPatient(item.diagnosedPerNewPatient) }}</td>
            <td>{{ formatMoney(item.totalDiagnosedAmount) }}</td>
            <td>{{ formatMoney(item.totalAcceptedAmount) }}</td>
            <td>{{ formatPercentage(item.acceptanceRate) }}</td>
            <td>{{ formatPercentage(item.acceptanceRatePatientBased) }}</td>
          </tr>

          <tr
            v-if="!userHasRecordInReport"
            class="median"
          >
            <td>Median</td>
            <td>{{ valuePerPatient(median.diagnosedPerRecallPatient) }}</td>
            <td>{{ valuePerPatient(median.diagnosedPerNewPatient) }}</td>
            <td>{{ formatMoney(median.totalDiagnosedAmount) }}</td>
            <td>{{ formatMoney(median.totalAcceptedAmount) }}</td>
            <td>{{ formatPercentage(median.acceptanceRate) }}</td>
            <td>{{ formatPercentage(median.acceptanceRatePatientBased) }}</td>
          </tr>
        </template>

        <!-- clientUser view -->
        <template v-else-if="!isAdmin && userRecord">
          <tr class="active">
            <td>{{ userRecord.fullName }}</td>
            <td>{{ valuePerPatient(userRecord.diagnosedPerRecallPatient) }}</td>
            <td>{{ valuePerPatient(userRecord.diagnosedPerNewPatient) }}</td>
            <td>{{ formatMoney(userRecord.totalDiagnosedAmount) }}</td>
            <td>{{ formatMoney(userRecord.totalAcceptedAmount) }}</td>
            <td>{{ formatPercentage(userRecord.acceptanceRate) }}</td>
            <td>{{ formatPercentage(userRecord.acceptanceRatePatientBased) }}</td>
          </tr>

          <tr v-if="median">
            <td>Median</td>
            <td>{{ valuePerPatient(median.diagnosedPerRecallPatient) }}</td>
            <td>{{ valuePerPatient(median.diagnosedPerNewPatient) }}</td>
            <td>{{ formatMoney(median.totalDiagnosedAmount) }}</td>
            <td>{{ formatMoney(median.totalAcceptedAmount) }}</td>
            <td>{{ formatPercentage(median.acceptanceRate) }}</td>
            <td>{{ formatPercentage(median.acceptanceRatePatientBased) }}</td>
          </tr>

          <tr v-if="averageTop10Percent">
            <td>Average top 10 percent</td>
            <td>{{ valuePerPatient(averageTop10Percent.diagnosedPerRecallPatient) }}</td>
            <td>{{ valuePerPatient(averageTop10Percent.diagnosedPerNewPatient) }}</td>
            <td>{{ formatMoney(averageTop10Percent.totalDiagnosedAmount) }}</td>
            <td>{{ formatMoney(averageTop10Percent.totalAcceptedAmount) }}</td>
            <td>{{ formatPercentage(averageTop10Percent.acceptanceRate) }}</td>
            <td>{{ formatPercentage(averageTop10Percent.acceptanceRatePatientBased) }}</td>
          </tr>
        </template>

        <NoRecordsFound
          v-else
          colspan="7"
        />
      </tbody>
    </ReportTable>

    <MetricDefinitionList
      v-if="!loading"
      :metrics="tooltips"
    />
  </ReportWrapper>
</template>

<script>
import DataService from '../../../../_services/DataService';
import MetricDefinitionList from '../../../components/Reporting/MetricDefinitionList';
import NoRecordsFound from '../../../components/Reporting/NoRecordsFound';
import ReportTable from '../../../components/Reporting/ReportTable.vue';
import ReportWrapper from '../../../components/Reporting/ReportWrapper';
import TableHeaderWithTooltip from '../../../components/Reporting/TableHeaderWithTooltip';
import Utils from '../../../utils';

export default {
  name: 'DDSTreatments',
  components: {
    MetricDefinitionList,
    NoRecordsFound,
    ReportTable,
    ReportWrapper,
    TableHeaderWithTooltip,
  },
  inject: ['shouldShowHiddenProviders'],
  props: ['tabLv1Index', 'tabLv2Index'],
  data() {
    return {
      treatmentsPerProviderAll: [],
      userRole: '',
      userRecord: null,
      averageTop10Percent: null,
      median: null,
      loading: false,
      tooltips: {
        'New patients': 'New patients having their first procedures completed within the selected time period',
        'Treatment-based acceptance rate':
          '(completed amount + scheduled amount) / total amount including treatment plan, completed & scheduled',
        'Patient-based acceptance rate':
          'Amount of treatment-accepting patients / total patients. For recall patients, it is calculated excluding recall/hygiene codes "D0120", "D1110", "D1120", "01110", "01120" & "D4910"',
      },
    };
  },
  computed: {
    isAdmin() {
      return this.userRole === 'clientAdmin';
    },
    userHasRecordInReport() {
      return !!this.userRecord;
    },
    showHiddenProviders() {
      return this.shouldShowHiddenProviders();
    },
    treatmentsPerProvider() {
      return this.showHiddenProviders
        ? this.treatmentsPerProviderAll
        : this.treatmentsPerProviderAll.filter(this.providerIsVisible);
    },
  },
  methods: {
    async loadReport(dateRange) {
      const { start, end } = dateRange;
      this.loading = true;

      const result = await DataService.getDDSTreatmentsReport({ start, end });

      if (result.status === 200) {
        const treatmentsPerProvider = result.data.treatmentsPerProvider ?? [];
        this.treatmentsPerProviderAll = treatmentsPerProvider;

        Utils.prependCurrentUserRecordAndMedian(treatmentsPerProvider, result.data.median);

        this.userRecord = result.data.userRecord;
        this.averageTop10Percent = result.data.averageTop10Percent;
        this.median = result.data.median;
      } else {
        this.treatmentsPerProviderAll = [];
      }

      this.loading = false;
    },
    formatMoney(number) {
      return Utils.formatCurrency(number, false, false);
    },
    formatPercentage(number) {
      return Utils.formatPercentage(number ?? 0);
    },
    valuePerPatient(value) {
      return value ? this.formatMoney(value) : '-';
    },
    providerIsVisible(provider) {
      return provider.isActive && !provider.isHidden && !provider.isHiddenReport;
    },
  },
  mounted() {
    this.userRole = DataService.getRole();
  },
};
</script>

<style scoped>
p {
  margin: 0;
}

th,
td {
  padding: 8px 16px !important;
}

th p {
  color: #718096;
}

.text-xs {
  font-size: 13px !important;
}

.font-normal {
  font-weight: normal !important;
}
</style>
